import React, { useMemo } from 'react'
import clsx from 'clsx'
import { Link, useLocation } from 'react-router-dom'
import { useLayout } from '../../../core'
import { usePageData } from '../../../core/PageData'
import { useAuth } from '../../../../../app/modules/auth'
import { getDefaultLandingRoute } from '../../../../../app/routing/routeHelper'

// Fallback home icon from SVG to avoid dependency on lucide-react if not installed
const HomeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
)

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
)

const PageTitle: React.FC = () => {
  const { pageTitle, pageBreadcrumbs } = usePageData()
  const { config, classes } = useLayout()
  const location = useLocation()
  const { currentUser } = useAuth()
  const homeRoute = getDefaultLandingRoute(currentUser)

  // Resolve crumbs & active title
  const { crumbs, activeTitle } = useMemo(() => {
    let finalTitle = pageTitle && pageTitle.trim() !== '' ? pageTitle : 'Dashboard'
    
    // Always fallback to something if pageTitle is not provided
    if (!pageTitle || pageTitle.trim() === '') {
       if (location.pathname.includes('/clients')) finalTitle = 'Clients Management'
       else if (location.pathname.includes('/billing')) finalTitle = 'Billing Rates'
       else if (location.pathname.includes('/products')) finalTitle = 'Products & Services'
       else if (location.pathname.includes('/cdr')) finalTitle = 'CDR Report'
       else if (location.pathname.includes('/data-sources')) finalTitle = 'Data Sources'
       else if (location.pathname.includes('/invoice')) finalTitle = 'Invoices'
    }

    // Try to use pageBreadcrumbs from context first
    if (pageBreadcrumbs && pageBreadcrumbs.length > 0) {
      const filtered = pageBreadcrumbs
        .filter(b => !b.isSeparator && b.title && b.title.trim() !== '')
        .map(b => ({
          title: b.title,
          path: b.path && b.path.trim() !== '' ? b.path : undefined
        }))
      if (filtered.length > 0) {
        return { crumbs: filtered, activeTitle: finalTitle }
      }
    }

    // Auto generate based on pathname if not provided
    const segments = location.pathname.replace(/^\/apps\//, '').split('/').filter(Boolean)
    if (segments.length > 0) {
      if (segments.length === 1 && segments[0] === 'dashboard') {
         return { crumbs: [], activeTitle: finalTitle }
      }
      const autoCrumbs = segments.slice(0, -1).map((s, idx) => ({
        title: s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' '),
        path: idx === 0 ? `/${s}` : undefined
      }))
      // If we don't have any crumbs but we are deep, let's at least add a parent
      if (autoCrumbs.length === 0) {
          if (location.pathname.includes('/clients') || location.pathname.includes('/billing') || location.pathname.includes('/products') || location.pathname.includes('/cdr') || location.pathname.includes('/data-sources') || location.pathname.includes('/invoice')) {
             autoCrumbs.push({ title: 'Apps', path: '#' })
          } else if (location.pathname.includes('/activity-log') || location.pathname.includes('/settings') || location.pathname.includes('/roles') || location.pathname.includes('/users')) {
             autoCrumbs.push({ title: 'System', path: '#' })
          }
      }
      return { crumbs: autoCrumbs, activeTitle: finalTitle }
    }

    return { crumbs: [], activeTitle: finalTitle }
  }, [location.pathname, pageTitle, pageBreadcrumbs])

  if (!config.app?.pageTitle?.display) {
    return null
  }

  return (
    <div
      id='sc_page_title'
      className={clsx(
        'page-title d-flex flex-wrap align-items-center',
        classes.pageTitle.join(' '),
        config.app?.pageTitle?.class
      )}
    >
      <div className='ccs-breadcrumb-pill-container ccs-breadcrumb-container d-inline-flex align-items-center gap-2.5 px-3.5 py-1.5 rounded-pill my-1'>
        {/* Home Circle Button */}
        <Link
          to={homeRoute}
          className='ccs-breadcrumb-home-circle ccs-breadcrumb-home-btn d-flex align-items-center justify-content-center text-decoration-none'
          title='Dashboard'
        >
          <HomeIcon />
        </Link>

        {/* Dynamic Breadcrumb Hierarchy Trail */}
        {crumbs && crumbs.length > 0 && (
          crumbs.map((crumb, idx) => (
            <React.Fragment key={`${crumb.title}-${idx}`}>
              <ChevronRightIcon className='text-gray-400 mx-1.5 ccs-breadcrumb-chevron' />
              {crumb.path ? (
                <Link
                  to={crumb.path}
                  className='ccs-breadcrumb-item-link ccs-breadcrumb-link text-decoration-none fs-7 fw-semibold px-1'
                >
                  {crumb.title}
                </Link>
              ) : (
                <span className='ccs-breadcrumb-item-link ccs-breadcrumb-text fs-7 fw-semibold px-1'>{crumb.title}</span>
              )}
            </React.Fragment>
          ))
        )}

        {/* Current Active Page Pill Badge with Glowing Dot */}
        {activeTitle && (
          <>
            <ChevronRightIcon className='text-gray-400 mx-1.5 ccs-breadcrumb-chevron' />
            <span className='ccs-breadcrumb-active-badge ccs-breadcrumb-current d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill fw-bold fs-7'>
              <span className='ccs-breadcrumb-blue-dot ccs-breadcrumb-active-dot me-1'></span>
              {activeTitle}
            </span>
          </>
        )}
      </div>
    </div>
  )
}

export { PageTitle }
